// Cache SWR en memoria para el frontend worker de Agorax.
//
// - `cacheGet`/`cacheSet`: acceso directo con TTL por clave.
// - `invalidate(prefix)`: borra todas las claves que empiezan por `prefix`.
// - `cachedFetch`: stale-while-revalidate; devuelve cache al instante (si existe)
//   y revalida en background llamando a `onData` de nuevo con los datos frescos.
//
// La invalidacion llega por Socket.IO (eventos `inventario_actualizado`,
// `pedido_actualizado`, etc.). Sin dependencias externas.

const DEFAULT_TTL = 30000;

const store = new Map(); // key -> { data, ts, ttl }

function isFresh(entry) {
  if (!entry) return false;
  const ttl = entry.ttl ?? DEFAULT_TTL;
  if (ttl === 0) return true; // sin expiracion
  return Date.now() - entry.ts < ttl;
}

export function cacheGet(key) {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (!isFresh(entry)) {
    store.delete(key);
    return undefined;
  }
  return entry.data;
}

export function cacheSet(key, data, ttl = DEFAULT_TTL) {
  store.set(key, { data, ts: Date.now(), ttl });
}

export function invalidate(prefix) {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}

export function cacheClear() {
  store.clear();
}

// SWR: si hay cache, entrega al instante y revalida en background; si no,
// espera el fetcher. `onData` se invoca con cache y luego con datos frescos.
export function cachedFetch(key, fetcher, { ttl = DEFAULT_TTL, onData, onError } = {}) {
  const hit = cacheGet(key);
  if (hit !== undefined) {
    if (onData) {
      try { onData(hit); } catch (_) {}
    }
    fetcher()
      .then((fresh) => {
        cacheSet(key, fresh, ttl);
        if (onData) onData(fresh);
      })
      .catch((err) => { if (onError) onError(err); });
    return Promise.resolve(hit);
  }
  return fetcher()
    .then((fresh) => {
      cacheSet(key, fresh, ttl);
      if (onData) onData(fresh);
      return fresh;
    })
    .catch((err) => {
      if (onError) onError(err);
      throw err;
    });
}
